/**
 * Reset-password — consume the single-use token from the email link
 * and pick a new password.
 *
 * The token is in the URL query string (`?token=...`). On success the
 * backend revokes EVERY active session for the account, so we hand
 * the user back to the login screen with a success message — they
 * have to log in fresh with the new password.
 */

import {
  Alert,
  Anchor,
  Button,
  Container,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../lib/api";

export function ResetPasswordScreen() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <Container size="xs" py="xl">
        <Paper p="lg" radius="md" withBorder>
          <Stack>
            <Title order={2}>קישור לא תקין</Title>
            <Text>
              הקישור חסר את הטוקן. בקש/י קישור חדש בעמוד "שכחתי סיסמה".
            </Text>
            <Group justify="center">
              <Anchor component={Link} to="/forgot-password" size="sm">
                שכחתי סיסמה
              </Anchor>
            </Group>
          </Stack>
        </Paper>
      </Container>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw1.length < 12) {
      setError("הסיסמה חייבת להיות באורך 12 תווים לפחות.");
      return;
    }
    if (pw1 !== pw2) {
      setError("הסיסמאות אינן תואמות.");
      return;
    }
    setBusy(true);
    try {
      await api.resetPasswordViaToken(token, pw1);
      setDone(true);
      // Brief delay so the user reads the success line.
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      if (err instanceof ApiError && err.detail === "reset_token_invalid_or_expired") {
        setError(
          "הקישור פג תוקף או כבר נוצל. בקש/י קישור חדש בעמוד \"שכחתי סיסמה\".",
        );
      } else {
        setError("משהו השתבש. נסה/י שוב.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container size="xs" py="xl">
      <Paper p="lg" radius="md" withBorder>
        <Stack>
          <Title order={2}>בחירת סיסמה חדשה</Title>
          {done ? (
            <Alert color="green">
              הסיסמה הוחלפה. כל ההתחברויות הפעילות נותקו — מעביר אותך למסך הכניסה...
            </Alert>
          ) : (
            <form onSubmit={submit}>
              <Stack>
                <Text c="dimmed" size="sm">
                  בחר/י סיסמה חדשה (לפחות 12 תווים). שמירת הסיסמה תנתק את כל
                  ההתחברויות הפעילות בחשבון.
                </Text>
                <PasswordInput
                  label="סיסמה חדשה"
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  required
                  autoFocus
                />
                <PasswordInput
                  label="סיסמה חדשה (אישור)"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                />
                {error && <Alert color="red">{error}</Alert>}
                <Button type="submit" loading={busy} fullWidth disabled={!pw1 || !pw2}>
                  קבע סיסמה חדשה
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
